import { Controller } from "@hotwired/stimulus"

// Simple contenteditable editor for the country landing page's welcome text.
// The hidden input arrives sanitized to the tags the portal shows
// (ApplicationHelper#welcome_text_for_editor), so loading it is safe.
export default class extends Controller {
  static targets = ["editor", "input"]

  connect() {
    this.editorTarget.innerHTML = this.inputTarget.value || ""
    // Ensure empty contenteditable has a focusable node
    if (!this.editorTarget.innerHTML.trim()) {
      this.editorTarget.innerHTML = "<p><br></p>"
    }

    this._setupSelectionTracking()
    this.form = this.element.closest("form")
    if (this.form) {
      // Form submissions must use the synchronous sync to ensure the input value is current.
      this._syncAll = () => { this.syncNow() }
      this.form.addEventListener("turbo:submit-start", this._syncAll)
      this.form.addEventListener("submit", this._syncAll)
      this.form.addEventListener("formdata", this._syncAll)
    }
  }

  disconnect() {
    if (this.form) {
      this.form.removeEventListener("turbo:submit-start", this._syncAll)
      this.form.removeEventListener("submit", this._syncAll)
      this.form.removeEventListener("formdata", this._syncAll)
    }
    if (this._onSelectionChange) document.removeEventListener("selectionchange", this._onSelectionChange)
    if (this._syncDebounce) { clearTimeout(this._syncDebounce); this._syncDebounce = null }
  }

  // Track the latest editor selection so toolbar actions can restore it after
  // focus shifts to a button, and remember the block it sits in.
  _setupSelectionTracking() {
    this._onSelectionChange = () => {
      const sel = window.getSelection()
      if (sel && sel.rangeCount && this.editorTarget.contains(sel.anchorNode) && this.editorTarget.contains(sel.focusNode)) {
        this._savedRange = sel.getRangeAt(0).cloneRange()
        this._activeBlock = this._topLevelBlockOf(sel.anchorNode) || this._activeBlock
      }
    }
    document.addEventListener("selectionchange", this._onSelectionChange)
  }

  _topLevelBlockOf(node) {
    while (node && node !== this.editorTarget && node.parentNode !== this.editorTarget) {
      node = node.parentNode
    }
    if (!node || node === this.editorTarget) return null
    return node
  }

  // Debounced for input events. Immediate sync available via syncNow.
  sync() {
    if (this._syncDebounce) clearTimeout(this._syncDebounce)
    this._syncDebounce = setTimeout(() => this.syncNow(), 150)
  }

  syncNow() {
    if (this._syncDebounce) { clearTimeout(this._syncDebounce); this._syncDebounce = null }
    this.inputTarget.value = this.editorTarget.innerHTML
  }

  bold(e) {
    e.preventDefault()
    document.execCommand("bold")
    this.sync()
  }

  italic(e) {
    e.preventDefault()
    document.execCommand("italic")
    this.sync()
  }

  // Toolbar button (reads data-tag).
  formatBlock(e) {
    e.preventDefault()
    const tag = e.currentTarget.dataset.tag
    if (!tag) return
    this._restoreSelection()
    document.execCommand("formatBlock", false, tag)
    this.sync()
  }

  bulletList(e) {
    e.preventDefault()
    document.execCommand("insertUnorderedList")
    this.sync()
  }

  alignLeft(e) { e?.preventDefault(); this._applyAlign("left") }
  alignCenter(e) { e?.preventDefault(); this._applyAlign("center") }
  alignRight(e) { e?.preventDefault(); this._applyAlign("right") }

  // Apply text-align to the enclosing block (and any selected blocks).
  _applyAlign(align) {
    this._restoreSelection()
    const sel = window.getSelection()
    if (!sel.rangeCount) {
      // Fall back to the last block the caret was in, if it is still there
      if (!this._activeBlock || !this.editorTarget.contains(this._activeBlock)) return
      this._activeBlock.style.textAlign = align
      this.sync()
      return
    }
    const range = sel.getRangeAt(0)
    if (!this.editorTarget.contains(range.commonAncestorContainer)) return

    const blocks = range.collapsed
      ? new Set([this._enclosingBlockFor(range.startContainer)].filter(Boolean))
      : this._blocksInRange(range)
    blocks.forEach(b => { b.style.textAlign = align })

    this.sync()
  }

  _enclosingBlockFor(node) {
    const blockTags = ["LI", "P", "H1", "H2", "H3", "H4", "H5", "H6", "DIV", "BLOCKQUOTE"]
    while (node && node !== this.editorTarget) {
      if (node.nodeType === 1 && blockTags.includes(node.tagName)) return node
      node = node.parentNode
    }
    return null
  }

  _blocksInRange(range) {
    const blockTags = new Set(["LI", "P", "H1", "H2", "H3", "H4", "H5", "H6", "BLOCKQUOTE"])
    const blocks = new Set()
    const startBlock = this._enclosingBlockFor(range.startContainer)
    const endBlock = this._enclosingBlockFor(range.endContainer)
    if (startBlock) blocks.add(startBlock)
    if (endBlock) blocks.add(endBlock)
    const walker = document.createTreeWalker(this.editorTarget, NodeFilter.SHOW_ELEMENT, {
      acceptNode: (node) => blockTags.has(node.tagName) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP
    })
    let n
    while ((n = walker.nextNode())) {
      if (range.intersectsNode(n)) blocks.add(n)
    }
    return blocks
  }

  captureSelection() {
    const sel = window.getSelection()
    if (sel && sel.rangeCount && this.editorTarget.contains(sel.anchorNode)) {
      this._savedRange = sel.getRangeAt(0).cloneRange()
    }
  }

  _restoreSelection() {
    if (!this._savedRange) return false
    const sel = window.getSelection()
    sel.removeAllRanges()
    sel.addRange(this._savedRange)
    return true
  }

  textColor(e) {
    if (e.type === "click") e.preventDefault()
    const color = e.currentTarget.dataset.color || e.currentTarget.value
    if (!color) return
    if (!this._restoreSelection()) return
    document.execCommand("styleWithCSS", false, true)
    document.execCommand("foreColor", false, color)
    this._collapseSelectionToEnd()
    this.sync()
  }

  _collapseSelectionToEnd() {
    const sel = window.getSelection()
    if (sel && sel.rangeCount) sel.collapseToEnd()
    this._savedRange = null
  }

  clearColor(e) {
    e.preventDefault()
    this._restoreSelection()
    const sel = window.getSelection()
    if (!sel.rangeCount) return
    const range = sel.getRangeAt(0)
    if (!this.editorTarget.contains(range.commonAncestorContainer)) return

    const stripStyles = (el) => {
      if (el.nodeType !== 1) return
      el.style.color = ""
      el.style.backgroundColor = ""
      el.style.background = ""
      if (el.tagName === "FONT") el.removeAttribute("color")
    }

    if (range.collapsed) {
      // Walk ancestors from caret up to editor
      let node = range.startContainer
      while (node && node !== this.editorTarget) {
        stripStyles(node)
        node = node.parentNode
      }
    } else {
      // Strip styles from all elements intersecting selection (and their descendants)
      const walker = document.createTreeWalker(this.editorTarget, NodeFilter.SHOW_ELEMENT)
      let n
      while ((n = walker.nextNode())) {
        if (range.intersectsNode(n)) stripStyles(n)
      }
      // Also strip on common ancestors above
      let node = range.commonAncestorContainer
      while (node && node !== this.editorTarget) {
        stripStyles(node)
        node = node.parentNode
      }
    }

    this._collapseSelectionToEnd()
    this.sync()
  }
}
